import { useState, useEffect } from "react";
import { UserRound, Shirt, X, ChevronsUpDown, Check, BookOpen } from "lucide-react";
import { ProxyImage } from "./ProxyImage";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";

interface PersonSummary {
  id: number;
  name: string;
  category?: string;
  analysisJson: string | null;
  primaryImageUrl: string | null;
}

interface PersonGarmentPickerProps {
  selectedPersonId: number | null;
  selectedGarmentId: number | null;
  onSelectPerson: (id: number | null) => void;
  onSelectGarment: (id: number | null) => void;
  onOpenLibrary: () => void;
}

export function PersonGarmentPicker({
  selectedPersonId,
  selectedGarmentId,
  onSelectPerson,
  onSelectGarment,
  onOpenLibrary,
}: PersonGarmentPickerProps) {
  const [persons, setPersons] = useState<PersonSummary[]>([]);
  const [garments, setGarments] = useState<PersonSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [personOpen, setPersonOpen] = useState(false);
  const [garmentOpen, setGarmentOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch("/api/persons").then((r) => r.ok ? r.json() : []),
      fetch("/api/garments").then((r) => r.ok ? r.json() : []),
    ]).then(([p, g]) => {
      setPersons(p);
      setGarments(g);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  const hasItems = persons.length > 0 || garments.length > 0;

  if (!hasItems) {
    return (
      <div className="space-y-1.5">
        <span className="text-[10px] text-muted-foreground/70">Iz biblioteke</span>
        <button
          onClick={onOpenLibrary}
          className="w-full py-2 text-[10px] text-muted-foreground/50 hover:text-primary/70 border border-dashed border-border/40 rounded-lg hover:border-primary/30 transition-all"
        >
          Dodaj osobe i odjeću u biblioteku
        </button>
      </div>
    );
  }

  const selectedPerson = persons.find((p) => p.id === selectedPersonId) ?? null;
  const selectedGarment = garments.find((g) => g.id === selectedGarmentId) ?? null;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground/70">Iz biblioteke</span>
        <button
          onClick={onOpenLibrary}
          className="text-[9px] text-muted-foreground/50 hover:text-primary transition-colors flex items-center gap-0.5"
        >
          <BookOpen className="w-2.5 h-2.5" />
          Otvori
        </button>
      </div>
      <div className="flex gap-1.5">
        {/* Person combobox */}
        {persons.length > 0 && (
          <Popover open={personOpen} onOpenChange={setPersonOpen}>
            <PopoverTrigger asChild>
              <button
                className={`flex-1 min-w-0 flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-left transition-all ${
                  selectedPerson
                    ? "border-emerald-500/40 bg-emerald-500/5"
                    : "border-border/40 hover:border-border"
                }`}
              >
                {selectedPerson ? (
                  <>
                    <div className="w-5 h-5 rounded-full overflow-hidden shrink-0 border border-emerald-500/30">
                      {selectedPerson.primaryImageUrl ? (
                        <ProxyImage src={{ type: "person", id: selectedPerson.id }} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-emerald-500/10">
                          <UserRound className="w-2.5 h-2.5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] font-medium truncate text-emerald-600 dark:text-emerald-400">{selectedPerson.name}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onSelectPerson(null); }}
                      className="ml-auto shrink-0 w-3.5 h-3.5 rounded-full bg-emerald-500/20 flex items-center justify-center hover:bg-emerald-500/40 transition-colors"
                    >
                      <X className="w-2 h-2 text-emerald-600 dark:text-emerald-400" />
                    </button>
                  </>
                ) : (
                  <>
                    <UserRound className="w-3.5 h-3.5 text-muted-foreground/30 shrink-0" />
                    <span className="text-[10px] text-muted-foreground/40 truncate">Osoba</span>
                    <ChevronsUpDown className="w-3 h-3 text-muted-foreground/30 ml-auto shrink-0" />
                  </>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Traži osobu..." className="h-8 text-[11px]" />
                <CommandList className="max-h-[200px]">
                  <CommandEmpty className="py-3 text-[11px]">Nema rezultata.</CommandEmpty>
                  <CommandGroup>
                    {persons.map((p) => (
                      <CommandItem
                        key={p.id}
                        value={p.name}
                        onSelect={() => {
                          onSelectPerson(p.id === selectedPersonId ? null : p.id);
                          setPersonOpen(false);
                        }}
                        className="flex items-center gap-2 px-2 py-1.5"
                      >
                        <div className="w-6 h-6 rounded-full overflow-hidden shrink-0 border border-border/50">
                          {p.primaryImageUrl ? (
                            <ProxyImage src={{ type: "person", id: p.id }} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-secondary/20">
                              <UserRound className="w-3 h-3 text-muted-foreground/30" />
                            </div>
                          )}
                        </div>
                        <span className="text-[11px] truncate">{p.name}</span>
                        {p.id === selectedPersonId && (
                          <Check className="w-3 h-3 ml-auto shrink-0 text-emerald-600 dark:text-emerald-400" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        {/* Garment combobox */}
        {garments.length > 0 && (
          <Popover open={garmentOpen} onOpenChange={setGarmentOpen}>
            <PopoverTrigger asChild>
              <button
                className={`flex-1 min-w-0 flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-left transition-all ${
                  selectedGarment
                    ? "border-primary/40 bg-primary/5"
                    : "border-border/40 hover:border-border"
                }`}
              >
                {selectedGarment ? (
                  <>
                    <div className="w-5 h-5 rounded overflow-hidden shrink-0 border border-primary/30">
                      {selectedGarment.primaryImageUrl ? (
                        <ProxyImage src={{ type: "garment", id: selectedGarment.id }} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-primary/10">
                          <Shirt className="w-2.5 h-2.5 text-primary" />
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] font-medium truncate text-primary">{selectedGarment.name}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onSelectGarment(null); }}
                      className="ml-auto shrink-0 w-3.5 h-3.5 rounded-full bg-primary/20 flex items-center justify-center hover:bg-primary/40 transition-colors"
                    >
                      <X className="w-2 h-2 text-primary" />
                    </button>
                  </>
                ) : (
                  <>
                    <Shirt className="w-3.5 h-3.5 text-muted-foreground/30 shrink-0" />
                    <span className="text-[10px] text-muted-foreground/40 truncate">Odjeća</span>
                    <ChevronsUpDown className="w-3 h-3 text-muted-foreground/30 ml-auto shrink-0" />
                  </>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Traži odjeću..." className="h-8 text-[11px]" />
                <CommandList className="max-h-[200px]">
                  <CommandEmpty className="py-3 text-[11px]">Nema rezultata.</CommandEmpty>
                  <CommandGroup>
                    {garments.map((g) => (
                      <CommandItem
                        key={g.id}
                        value={g.name}
                        onSelect={() => {
                          onSelectGarment(g.id === selectedGarmentId ? null : g.id);
                          setGarmentOpen(false);
                        }}
                        className="flex items-center gap-2 px-2 py-1.5"
                      >
                        <div className="w-6 h-6 rounded overflow-hidden shrink-0 border border-border/50">
                          {g.primaryImageUrl ? (
                            <ProxyImage src={{ type: "garment", id: g.id }} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-secondary/20">
                              <Shirt className="w-3 h-3 text-muted-foreground/30" />
                            </div>
                          )}
                        </div>
                        <span className="text-[11px] truncate">{g.name}</span>
                        {g.id === selectedGarmentId && (
                          <Check className="w-3 h-3 ml-auto shrink-0 text-primary" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
